import React from "react";
import { useHistory } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import { useAsyncEffect } from "@react-hook/async";
import * as Sentry from "@sentry/react";

import { finishUserAuth } from "@shared/api/lib/auth/auth";
import { LoadingScreen, RobotsNoindex } from "@shared/components";
import { LOGIN_STATE_SENTINEL } from "@shared/constants";
import {
  getRedirectPath,
  getUserData,
  isAuthenticated,
  recoverBrowserSecurityError,
} from "@shared/utils";

type RedirectParams = {
  authProviderName: string;
};

export const OAuthCallbackScreen: React.FC<
  RouteComponentProps<RedirectParams>
> = ({ match }) => {
  const history = useHistory();

  useAsyncEffect(async () => {
    const urlParams: URLSearchParams = new URLSearchParams(
      history.location.search
    );

    const validateSentinel = () => {
      const receivedSentinel = (urlParams.get("state") || "").split("-")[0];
      const sentSentinel: string | null = recoverBrowserSecurityError(() =>
        localStorage.getItem(LOGIN_STATE_SENTINEL)
      );
      recoverBrowserSecurityError(() =>
        localStorage.removeItem(LOGIN_STATE_SENTINEL)
      );

      if (receivedSentinel !== sentSentinel && sentSentinel !== null) {
        history.replace("/");
        throw new Error(
          `Our sentinel value is different from what we sent to oauth2 authorize endpoint`
        );
      }
    };

    /* Be careful editing this function. It gets called:
        - when a user signs in for the first time
        - when a user logs in after logging out
        - when a user adds an organization
    */
    const handleCallback = async () => {
      const authCode = urlParams.get("code");
      const authError = urlParams.get("error");
      const authErrorDesc = urlParams.get("error_description") || "";
      const authState = urlParams.get("state");
      const setupAction = urlParams.get("setup_action");
      const redirectUri = `${window.location.origin}${window.location.pathname}`;

      if (authError) {
        history.push({
          pathname: "/sso-error",
          state: {
            error: authError,
            desc: authErrorDesc,
            providerName: match.params.authProviderName,
          },
        });
        return;
      }

      if (authCode === null) {
        if (setupAction) {
          console.error(
            `Github auth callback has setupAction ${setupAction} and is missing code but we're probably already logged in, so let's exit early`
          );
          Sentry.captureMessage(
            `Got a weird request from Github with no code param but a setup_action:${setupAction}`
          );
          history.push(getRedirectPath(window.location.search));
          return;
        }
        console.error("there appears to be a missing 'code' param");
        Sentry.captureMessage("there appears to be a missing 'code' param");
        history.push("/fatal-error");
        return;
      }

      validateSentinel();
      try {
        const authProviderName = match.params.authProviderName;
        await finishUserAuth(
          redirectUri,
          authCode,
          authState,
          authProviderName,
          isAuthenticated()
        );
      } catch (err) {
        Sentry.captureException(err);
        console.error("could not finish user auth: " + err);
        history.push("/fatal-error");
        return;
      }
      // even though UserContext might not have had a user yet
      // we are guaranteed to get a result now after the above lines
      const user = getUserData();
      if (!user) return;

      // bring users back to wherever they signed in from
      history.push(getRedirectPath(authState));
      // refresh page so user changes are reflected
      history.go(0);
    };

    await handleCallback();
  }, []); // Important: This must be empty so that callback runs ONLY once

  return (
    <>
      <RobotsNoindex />
      <LoadingScreen text="Logging in…" />
    </>
  );
};
